/* eslint-disable react-refresh/only-export-components */
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { GenerationAssetsProvider, useGenerationAssetsSafe } from './assetsContext';
import type { AssetsContextValue } from './assetsContext';
import { GenerationJobsProvider, useGenerationJobsSafe } from './jobsContext';
import type { JobsContextValue } from './jobsContext';
import { ScenarioAttributesProvider, useScenarioAttributesStateSafe } from './scenarioAttributesContext';
import type { ScenarioAttributesContextValue } from './scenarioAttributesContext';
import { GenerationSettingsProvider, useGenerationSettingsSafe } from './settingsContext';
import type { SettingsContextValue } from './settingsContext';

// ========================================
// 定数・純関数・型は同ディレクトリのモジュールへ分離済み (ロジック変更なし)。
// state 本体も 4 つの Context (Assets / Settings / ScenarioAttributes / Jobs) へ
// 分割済み。既存の import 経路 (`from '.../state/useGenerationState'`) を壊さないよう
// ここから再エクスポートし、`useGenerationState()` は 4 Context を合成した
// 従来と同一形状のオブジェクトを返す後方互換ファサードとして残す。
// ========================================
export * from './modelCatalog';
export * from './imageFormat';
export * from './visualStyle';
export * from './types';
export { DEFAULT_SETTINGS, getLastMenuForPlatform, getLastSpecForMenu } from './settingsStorage';

// 個別 Context のフック。新規コード・再レンダーを絞りたい消費者はこちらを直接使う。
export {
  GenerationAssetsProvider,
  useGenerationAssets,
  useGenerationAssetsSafe,
  STILL_IMAGE_SCOPE,
} from './assetsContext';
export type { AssetsContextValue } from './assetsContext';
export { GenerationSettingsProvider, useGenerationSettings, useGenerationSettingsSafe } from './settingsContext';
export type { SettingsContextValue } from './settingsContext';
export {
  ScenarioAttributesProvider,
  useScenarioAttributesState,
  useScenarioAttributesStateSafe,
} from './scenarioAttributesContext';
export type { ScenarioAttributesContextValue } from './scenarioAttributesContext';
export { GenerationJobsProvider, useGenerationJobs, useGenerationJobsSafe } from './jobsContext';
export type { JobsContextValue } from './jobsContext';

/**
 * 旧・単一 Context と同一形状の合成型。4 Context のメンバ集合の直和で、
 * プロパティの増減は無い (分割前後で消費者のコードは無変更)。
 */
export type GenerationState =
  & AssetsContextValue
  & SettingsContextValue
  & ScenarioAttributesContextValue
  & JobsContextValue;

/**
 * 4 Context を合成する Provider。ネスト順には依存関係があるので入れ替えないこと。
 *
 *   ScenarioAttributes (ほぼ不変。visual_family マップを Settings へ供給)
 *     └ Settings      (visualStyleIsStylized を導出)
 *         └ Assets    (usage 決定に productionMode / videoGenerationMode /
 *                      maxReferenceVideos を、beginVideoEdit に Settings の setter を使う)
 *             └ Jobs  (他 3 つに依存しない独立ブロック)
 */
export function GenerationProvider({ children }: { children: ReactNode }) {
  return (
    <ScenarioAttributesProvider>
      <GenerationSettingsProvider>
        <GenerationAssetsProvider>
          <GenerationJobsProvider>
            {children}
          </GenerationJobsProvider>
        </GenerationAssetsProvider>
      </GenerationSettingsProvider>
    </ScenarioAttributesProvider>
  );
}

/** 4 Context を合成する。いずれかが Provider 外なら null。 */
function useMergedGenerationState(): GenerationState | null {
  const assets = useGenerationAssetsSafe();
  const settings = useGenerationSettingsSafe();
  const scenarioAttributes = useScenarioAttributesStateSafe();
  const jobs = useGenerationJobsSafe();
  return useMemo(() => {
    if (!assets || !settings || !scenarioAttributes || !jobs) return null;
    return { ...scenarioAttributes, ...settings, ...assets, ...jobs };
  }, [assets, settings, scenarioAttributes, jobs]);
}

/**
 * 後方互換ファサード。分割前と同じ 1 オブジェクトを返すため、既存の消費者は無変更で動く。
 * ただし 4 Context のどれが変わっても再レンダーするので、段階的に個別フック
 * (useGenerationAssets / useGenerationSettings / useScenarioAttributesState /
 * useGenerationJobs) へ移行していくこと。
 */
export function useGenerationState(): GenerationState {
  const merged = useMergedGenerationState();
  if (!merged) {
    throw new Error('useGenerationState must be used within GenerationProvider');
  }
  return merged;
}

/**
 * Safe version of useGenerationState that returns null if provider is not available
 */
export function useGenerationStateSafe(): GenerationState | null {
  return useMergedGenerationState();
}
